import { CircularProgress, MenuItem } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { Duration } from 'luxon';
import HoverMenu from 'material-ui-popup-state/HoverMenu';
import {
  usePopupState,
  bindHover,
  bindMenu,
} from 'material-ui-popup-state/hooks';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import SeekBar from '@@src/components/SeekBar/SeekBar';
import { Tracks } from '@@stores/chromeCastStore';
import orange from '@@styles/colors/orange';
import { PlaybackStreamData } from '@@types/PlaybackStreamData';

const useStyles = makeStyles((theme: Theme) => {
  return createStyles({
    container: {
      left: 0,
      boxSizing: 'border-box',
      position: 'fixed',
      bottom: 0,
      fontSize: '1rem',
      padding: '48px 30px 30px',
      zIndex: 999,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      backdropFilter: 'blur(10px)',
      width: '100%',
      height: 222,
      '> button': {
        marginRight: 5,
      },
    },

    topSection: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingBottom: '1.5rem',
    },

    controlsSection: {
      position: 'relative',
      height: 96, // align with the height of playbackControlsContainer
      display: 'flex',
    },

    mediaInfoSection: {
      display: 'none',
      zIndex: 9999,

      [theme.breakpoints.up('sm')]: {
        display: 'flex',
      },
    },

    mediaInfoImage: {
      height: 96,
      minWidth: 171,
      width: 171,
      borderRadius: 4,
      backgroundImage: 'url(\'https://image.pr.sbsod.com/7f1040dd-f13a-5f7f-a1c2-7a79a00974fd?crop=true&width=300&height=169&type=jpeg\')',
      backgroundSize: 'cover',
      marginRight: '1rem',
      display: 'none',

      [theme.breakpoints.up('md')]: {
        display: 'block',
      },
    },

    mediaInfoTextContainer: {
      maxWidth: 300,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      visibility: 'hidden',

      [theme.breakpoints.up('sm')]: {
        visibility: 'visible',
      },
      [theme.breakpoints.up('md')]: {
        maxWidth: 'none',
      },
    },

    mediaInfoGroup: {
      margin: '0.5rem 0 0',
    },

    mediaInfoTitle: {
      overflow: 'hidden',
      fontSize: '1rem',
      fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
      fontWeight: 500,
      lineHeight: 1.2,
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      margin: 0,
    },

    mediaInfoSubtitle: {
      fontWeight: 400,
      letterSpacing: 'normal',
      color: '#9ba4ac',
      fontSize: '0.875rem',
      lineHeight: 1.2,
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      textAlign: 'left',
      fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
      flexShrink: 0,
      margin: '0.25rem 0 0 0',
    },

    playbackControlsSection: {
      flexGrow: 1,
    },

    playbackControlsContainer: {
      position: 'absolute',
      width: '100%',
      left: 0,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },

    controlsButton: {
      border: 0,
      backgroundColor: 'transparent',
      backgroundOrigin: 'content-box',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      boxSizing: 'content-box',
      fontSize: '1em',
      height: '1.5em',
      minWidth: '1.5em',
      transition: 'opacity 0.2s ease-in-out',
      cursor: 'pointer',
      padding: '2.25em 2.25em 2.25em 2.25em',
      opacity: 0.65,

      '&:hover': {
        opacity: 1,
      },
    },

    pauseButton: {
      backgroundSize: 72,
      backgroundImage: 'url("data:image/svg+xml,%3C%3Fxml version=\'1.0\' encoding=\'UTF-8\'%3F%3E%3Csvg width=\'24px\' height=\'24px\' viewBox=\'0 0 24 24\' version=\'1.1\' xmlns=\'http://www.w3.org/2000/svg\' xmlns:xlink=\'http://www.w3.org/1999/xlink\'%3E%3Ctitle%3EIcon/Medium/Pause%3C/title%3E%3Cg id=\'Icon/Medium/Pause\' stroke=\'none\' stroke-width=\'1\' fill=\'none\' fill-rule=\'evenodd\'%3E%3Cpath d=\'M8.5,3 C9.6045695,3 10.5,3.8954305 10.5,5 L10.5,19 C10.5,20.1045695 9.6045695,21 8.5,21 C7.3954305,21 6.5,20.1045695 6.5,19 L6.5,5 C6.5,3.8954305 7.3954305,3 8.5,3 Z M15.5,3 C16.6045695,3 17.5,3.8954305 17.5,5 L17.5,19 C17.5,20.1045695 16.6045695,21 15.5,21 C14.3954305,21 13.5,20.1045695 13.5,19 L13.5,5 C13.5,3.8954305 14.3954305,3 15.5,3 Z\' id=\'Mask\' fill=\'%23FFFFFF\' fill-rule=\'nonzero\'%3E%3C/path%3E%3C/g%3E%3C/svg%3E")',
    },

    playButton: {
      backgroundSize: 72,
      backgroundImage: 'url("data:image/svg+xml,%3C%3Fxml version=\'1.0\' encoding=\'UTF-8\'%3F%3E%3Csvg width=\'24px\' height=\'24px\' viewBox=\'0 0 24 24\' version=\'1.1\' xmlns=\'http://www.w3.org/2000/svg\' xmlns:xlink=\'http://www.w3.org/1999/xlink\'%3E%3Ctitle%3EIcon/Medium/Play%3C/title%3E%3Cg id=\'Icon/Medium/Play\' stroke=\'none\' stroke-width=\'1\' fill=\'none\' fill-rule=\'evenodd\'%3E%3Cpath d=\'M6,21.0000239 C5.82391379,20.9981713 5.65143281,20.9498766 5.5,20.86 C5.19245546,20.6824477 5.00214346,20.3551111 5,20 L5,4 C5.00214346,3.64488894 5.19245546,3.3175523 5.5,3.14 C5.80940108,2.96136721 6.19059892,2.96136721 6.5,3.14 L20.5,11.14 C20.8084022,11.3188989 20.9982281,11.6484657 20.9982281,12.005 C20.9982281,12.3615343 20.8084022,12.6911011 20.5,12.87 L6.5,20.87 C6.34759709,20.9563804 6.17517638,21.0012098 6,21.0000239 Z\' id=\'Mask\' fill=\'%23FFFFFF\' fill-rule=\'nonzero\'%3E%3C/path%3E%3C/g%3E%3C/svg%3E")',
    },

    seekButton: {
      backgroundSize: 40,
      '&:disabled': {
        opacity: '0.4',
        cursor: 'not-allowed',
      },
    },

    seekBackButton: {
      backgroundImage: 'url("data:image/svg+xml,%3C%3Fxml version=\'1.0\' encoding=\'UTF-8\'%3F%3E%3Csvg width=\'40px\' height=\'40px\' viewBox=\'0 0 40 40\' version=\'1.1\' xmlns=\'http://www.w3.org/2000/svg\' xmlns:xlink=\'http://www.w3.org/1999/xlink\'%3E%3Ctitle%3Eicon - back 10%3C/title%3E%3Cg id=\'icon---back-10\' stroke=\'none\' stroke-width=\'1\' fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg id=\'back-10-seconds\' transform=\'translate(2.500000, 0.000000)\'%3E%3Cpath d=\'M11.1632051,0.390002167 C11.6891026,-0.130000722 12.5416667,-0.130000722 13.0675641,0.390002167 C13.5925641,0.910893949 13.5925641,1.75534309 13.0675641,2.27623487 L9.97948718,5.33314074 L17.5,5.33314074 C27.1492308,5.33314074 35,13.1091839 35,22.6665704 C35,32.2239568 27.1492308,40 17.5,40 C7.85076923,40 0,32.2239568 0,22.6665704 C0,21.9296774 0.602179487,21.3332296 1.34615385,21.3332296 C2.09012821,21.3332296 2.69230769,21.9296774 2.69230769,22.6665704 C2.69230769,30.7537264 9.33512821,37.3333185 17.5,37.3333185 C25.6648718,37.3333185 32.3076923,30.7537264 32.3076923,22.6665704 C32.3076923,14.5794143 25.6648718,7.99982222 17.5,7.99982222 L9.98217949,7.99982222 L13.0675641,11.0567281 C13.5925641,11.5776199 13.5925641,12.422069 13.0675641,12.9429608 C12.8046154,13.2034067 12.46,13.3331852 12.1153846,13.3331852 C11.7707692,13.3331852 11.4261538,13.2034067 11.1632051,12.9429608 L5.77858974,7.60604226 C5.52641026,7.35626309 5.38461538,7.01759454 5.38461538,6.6638148 C5.38461538,6.31003506 5.52641026,5.97047761 5.77858974,5.72069845 L11.1632051,0.390002167 Z\' id=\'Combined-Shape-path\' fill=\'%23FFFFFF\' fill-rule=\'nonzero\'%3E%3C/path%3E%3Cg id=\'Group\' transform=\'translate(10.900000, 17.500000)\'%3E%3Cpath d=\'M3.10503263,0 C3.66003849,0 4.32004542,0.208003378 4.32004542,1.20344793 L4.32004542,1.20344793 L4.32004542,9.38899109 C4.32004542,10.2358618 3.81004005,10.6667259 3.10503263,10.6667259 C2.40002521,10.6667259 1.89001983,10.2358618 1.89001983,9.38899109 L1.89001983,9.38899109 L1.89001983,2.22860723 L1.12501182,2.22860723 C0.37500391,2.22860723 0,1.66402676 0,1.11430361 C0,0.56458047 0.37500391,0 1.12501182,0 L1.12501182,0 L3.10503263,0 Z\' id=\'Combined-Shape-path\' fill=\'%23FFFFFF\' fill-rule=\'nonzero\'%3E%3C/path%3E%3Cpath d=\'M10.0856684,1.11114662 C10.5443469,1.12192917 11.4125144,1.27463224 12.0208892,2.13492964 C12.4880244,2.79550149 12.7884615,3.80992366 12.7884615,5.31585415 C12.7884615,6.84382611 12.4809381,7.8703871 12.0041853,8.53624653 C11.3796587,9.4084952 10.4882208,9.55560864 10.0790534,9.55560864 C9.67326895,9.55560864 8.79155891,9.44293406 8.17640815,8.57946295 C7.70489165,7.91760736 7.40384615,6.88674208 7.40384615,5.33697276 C7.40384615,3.79601496 7.70803896,2.76776044 8.18155863,2.10517685 C8.80079765,1.23869204 9.68550468,1.11328298 10.0856684,1.11114662 Z\' id=\'Path-4\' stroke=\'%23FFFFFF\' stroke-width=\'2.22223457\'%3E%3C/path%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
    },

    seekForwardButton: {
      backgroundImage: 'url("data:image/svg+xml,%3C%3Fxml version=\'1.0\' encoding=\'UTF-8\'%3F%3E%3Csvg width=\'40px\' height=\'40px\' viewBox=\'0 0 40 40\' version=\'1.1\' xmlns=\'http://www.w3.org/2000/svg\' xmlns:xlink=\'http://www.w3.org/1999/xlink\'%3E%3Ctitle%3Eicon - fwd 10%3C/title%3E%3Cg id=\'icon---fwd-10\' stroke=\'none\' stroke-width=\'1\' fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg id=\'forward-10\' transform=\'translate(2.500000, 0.000000)\'%3E%3Cpath d=\'M33.6538462,21.3332296 C32.9098718,21.3332296 32.3076923,21.9296774 32.3076923,22.6665704 C32.3076923,30.7537264 25.6648718,37.3333185 17.5,37.3333185 C9.33512821,37.3333185 2.69230769,30.7537264 2.69230769,22.6665704 C2.69230769,14.5794143 9.33512821,7.99982222 17.5,7.99982222 L25.0178205,7.99982222 L21.9324359,11.0567281 C21.4074359,11.5776199 21.4074359,12.422069 21.9324359,12.9429608 C22.1953846,13.2034067 22.54,13.3331852 22.8846154,13.3331852 C23.2292308,13.3331852 23.5738462,13.2034067 23.8367949,12.9429608 L29.2214103,7.60604226 C29.4735897,7.35626309 29.6153846,7.01759454 29.6153846,6.6638148 C29.6153846,6.31003506 29.4735897,5.97047761 29.2214103,5.72069845 L23.8367949,0.390002167 C23.3108974,-0.130000722 22.4583333,-0.130000722 21.9324359,0.390002167 C21.4074359,0.910893949 21.4074359,1.75534309 21.9324359,2.27623487 L25.0205128,5.33314074 L17.5,5.33314074 C7.85076923,5.33314074 0,13.1091839 0,22.6665704 C0,32.2239568 7.85076923,40 17.5,40 C27.1492308,40 35,32.2239568 35,22.6665704 C35,21.9296774 34.3978205,21.3332296 33.6538462,21.3332296\' id=\'Combined-Shape\' fill=\'%23FFFFFF\' fill-rule=\'nonzero\'%3E%3C/path%3E%3Cg id=\'Group-2\' transform=\'translate(10.200000, 17.500000)\'%3E%3Cpath d=\'M3.10503263,10.6667259 C3.81004005,10.6667259 4.32004542,10.2358618 4.32004542,9.38899109 L4.32004542,1.20344793 C4.32004542,0.208003378 3.66003849,0 3.10503263,0 L1.12501182,0 C0.37500391,0 0,0.56458047 0,1.11430361 C0,1.66402676 0.37500391,2.22860723 1.12501182,2.22860723 L1.89001983,2.22860723 L1.89001983,9.38899109 C1.89001983,10.2358618 2.40002521,10.6667259 3.10503263,10.6667259\' id=\'Fill-3\' fill=\'%23FFFFFF\' fill-rule=\'nonzero\'%3E%3C/path%3E%3Cpath d=\'M10.0961538,1.11111728 C10.4917923,1.11111728 11.384925,1.24845022 12.0097506,2.1192924 C12.483139,2.77907126 12.7884615,3.79764705 12.7884615,5.31585415 C12.7884615,6.84382611 12.4809381,7.8703871 12.0041853,8.53624653 C11.3796587,9.4084952 10.4882208,9.55560864 10.0790534,9.55560864 C9.67326895,9.55560864 8.79155891,9.44293406 8.17640815,8.57946295 C7.70489165,7.91760736 7.40384615,6.88674208 7.40384615,5.33697276 C7.40384615,3.79601496 7.70803896,2.76776044 8.18155863,2.10517685 C8.80628844,1.23100897 9.70121492,1.11111728 10.0961538,1.11111728 Z\' id=\'Path-4\' stroke=\'%23FFFFFF\' stroke-width=\'2.22223457\'%3E%3C/path%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
    },

    settingsSection: {
      whiteSpace: 'nowrap',
      zIndex: 9999,
    },

    settingsSubtitles: {
      backgroundImage: 'url("data:image/svg+xml,%3C%3Fxml version=\'1.0\' encoding=\'UTF-8\'%3F%3E%3Csvg width=\'48px\' height=\'48px\' viewBox=\'0 0 48 48\' version=\'1.1\' xmlns=\'http://www.w3.org/2000/svg\' xmlns:xlink=\'http://www.w3.org/1999/xlink\'%3E%3Ctitle%3Eicon - subtitles%3C/title%3E%3Cg id=\'icon---subtitles\' stroke=\'none\' stroke-width=\'1\' fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg id=\'subtitle-icon\' transform=\'translate(7.290000, 7.500000)\'%3E%3Cpath d=\'M24,30.5 L27.9713201,34.2193965 C28.3744205,34.5969259 29.0072461,34.5761966 29.3847755,34.1730961 C29.5583275,33.9877891 29.6548976,33.7434064 29.6548976,33.4895186 L29.6548976,30.5 L29.6548976,30.5 L24,30.5 Z\' id=\'audio\' stroke=\'%23FFFFFF\' stroke-width=\'1.3635\' fill=\'%23FFFFFF\' stroke-linejoin=\'round\'%3E%3C/path%3E%3Cg id=\'Group\' transform=\'translate(5.000000, 17.000000)\' fill=\'%23FFFFFF\' fill-rule=\'nonzero\'%3E%3Cpath d=\'M13.3333333,5 C14.2538079,5 15,5.74619208 15,6.66666667 C15,7.58714125 14.2538079,8.33333333 13.3333333,8.33333333 L1.66666667,8.33333333 C0.746192084,8.33333333 0,7.58714125 0,6.66666667 C0,5.74619208 0.746192084,5 1.66666667,5 L13.3333333,5 Z\' id=\'Path\'%3E%3C/path%3E%3Cpath d=\'M21.6666667,5 C22.5871412,5 23.3333333,5.74619208 23.3333333,6.66666667 C23.3333333,7.58714125 22.5871412,8.33333333 21.6666667,8.33333333 L18.3333333,8.33333333 C17.4128588,8.33333333 16.6666667,7.58714125 16.6666667,6.66666667 C16.6666667,5.74619208 17.4128588,5 18.3333333,5 L21.6666667,5 Z\' id=\'Path\'%3E%3C/path%3E%3Cpath d=\'M21.6666667,0 C22.5871412,0 23.3333333,0.746192084 23.3333333,1.66666667 C23.3333333,2.58714125 22.5871412,3.33333333 21.6666667,3.33333333 L10,3.33333333 C9.07952542,3.33333333 8.33333333,2.58714125 8.33333333,1.66666667 C8.33333333,0.746192084 9.07952542,0 10,0 L21.6666667,0 Z\' id=\'Path\'%3E%3C/path%3E%3Cpath d=\'M5,0 C5.92047458,0 6.66666667,0.746192084 6.66666667,1.66666667 C6.66666667,2.58714125 5.92047458,3.33333333 5,3.33333333 L1.66666667,3.33333333 C0.746192084,3.33333333 0,2.58714125 0,1.66666667 C0,0.746192084 0.746192084,0 1.66666667,0 L5,0 Z\' id=\'Path\'%3E%3C/path%3E%3C/g%3E%3Cpath d=\'M0,29 L0,2.63409447 C7.33139281e-06,2.16231008 0.329746727,1.75470469 0.791124454,1.65615974 C5.9604423,0.552053247 11.2650188,0 16.7048539,0 C22.1446747,0 27.4492375,0.552050333 32.6185424,1.656151 C33.0799519,1.75465959 33.4097078,2.16228518 33.4097078,2.63409302 L33.4097078,29 C33.4097078,29.5522847 32.9619926,30 32.4097078,30 L1,30 C0.44771525,30 -4.33869274e-17,29.5522847 0,29 Z\' id=\'Path-5\' stroke=\'%23FFFFFF\' stroke-width=\'3.3\'%3E%3C/path%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
      paddingRight: '6px',
    },

    castGroup: {
      height: 40,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      transform: 'translateY(4px)',
    },
    toggleButton: {
      backgroundColor: 'white',
      border: 'none',
      maskImage: 'url("data:image/svg+xml,%3C%3Fxml version=\'1.0\' encoding=\'UTF-8\'%3F%3E%3Csvg width=\'48px\' height=\'48px\' viewBox=\'0 0 48 48\' version=\'1.1\' xmlns=\'http://www.w3.org/2000/svg\' xmlns:xlink=\'http://www.w3.org/1999/xlink\'%3E%3Ctitle%3Eicon - chromecast%3C/title%3E%3Cdefs%3E%3Cpath d=\'M38.2347329,7.66666667 C41.4595785,7.66666667 44.0833333,10.3132199 44.0833333,13.5660868 L44.0833333,13.5660868 L44.0833333,33.3922465 C44.0833333,36.6451135 41.4595785,39.2916667 38.2347329,39.2916667 L38.2347329,39.2916667 L22.5190422,39.2916667 L22.5190422,35.4243215 L38.2347329,35.4243215 C39.3453237,35.4243215 40.2493029,34.5129172 40.2493029,33.3922465 L40.2493029,33.3922465 L40.2493029,13.5660868 C40.2493029,12.4454161 39.3453237,11.5340118 38.2347329,11.5340118 L38.2347329,11.5340118 L7.76569309,11.5340118 C6.65467627,11.5340118 5.7506971,12.4454161 5.7506971,13.5660868 L5.7506971,13.5660868 L5.7506971,18.6976241 L1.91666667,18.6976241 L1.91666667,13.5660868 C1.91666667,10.3132199 4.54042149,7.66666667 7.76569309,7.66666667 L7.76569309,7.66666667 Z M1.91666667,35.7777778 C3.85759659,35.7777778 5.43055556,37.3274602 5.43055556,39.2385226 L5.43055556,39.2385226 L1.91666667,39.2916667 Z M1.91666667,28.75 C7.7293376,28.75 12.4583333,33.4789957 12.4583333,39.2916667 L12.4583333,39.2916667 L8.64257561,39.2916667 C8.64257561,35.5827502 5.62558317,32.5657577 1.91666667,32.5657577 L1.91666667,32.5657577 Z M1.91666667,21.7222222 C11.6046379,21.7222222 19.4861111,29.6039294 19.4861111,39.2916667 L19.4861111,39.2916667 L15.6677734,39.2916667 C15.6677734,31.7090554 9.49903678,25.5404677 1.91666667,25.5404677 L1.91666667,25.5404677 Z\' id=\'path-1\'%3E%3C/path%3E%3C/defs%3E%3Cg id=\'icon---chromecast\' stroke=\'none\' stroke-width=\'1\' fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg id=\'icon-/-01-small-/-37-mirror-copy\' transform=\'translate(1.000000, 1.000000)\'%3E%3Cmask id=\'mask-2\' fill=\'white\'%3E%3Cuse xlink:href=\'%23path-1\'%3E%3C/use%3E%3C/mask%3E%3Cuse id=\'Combined-Shape\' fill=\'%23FFFFFF\' xlink:href=\'%23path-1\'%3E%3C/use%3E%3Cg id=\'Group\' mask=\'url(%23mask-2)\' fill=\'%23FFFFFF\'%3E%3Cg id=\'colour-/-D5D9DC\'%3E%3Crect id=\'Rectangle\' x=\'0\' y=\'0\' width=\'46\' height=\'46\'%3E%3C/rect%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
      height: 48,
      width: 48,
      padding: '0.5rem',
      cursor: 'pointer',
      opacity: 0.8,
      '&:hover, &:focus': {
        opacity: 1,
      },
      '&:focus-visible': {
        boxShadow: '0 0 0 3px #ff0000',
      },
    },
    toggleButtonActive: {
      backgroundColor: orange.darkTangerine,
    },

    statusLabel: {
      color: orange.darkTangerine,
      padding: 0,
      margin: '0 0 0 1rem',
      textAlign: 'right',
      overflow: 'hidden',
      fontSize: '1rem',
      fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
      fontWeight: 500,
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
    },

    smCastContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-around',
      zIndex: 9999,

      [theme.breakpoints.up('sm')]: {
        display: 'none',
      },
    },
    subtitlesMenuHeading: {
      margin: '0 0 0.5rem 0',
      fontSize: '1.2rem',
      fontWeight: 700,
      cursor: 'default',
    },
    activeTrack: {
      color: orange.darkTangerine,
    },
    loadingSpinner: {
      marginTop: 20,
      textAlign: 'center',
    },

    chevronIcon: {
      marginLeft: '10px',
      '& path': {
        transition: 'opacity 0.2s ease-in-out',
        opacity: 0.8,
      },
      '&:hover, &:focus': {
        '& path': {
          opacity: 1,
        },
      },
    },

    minimised: {
      transform: 'translateY(122px)',
    },
  });
});

export type RemotePlayerProps = {
  currentTime: number
  duration: number
  playbackData: PlaybackStreamData | null
  tracks: Array<Tracks & { active: boolean }> | null
  timelineMarkers: number[] | null

  isPaused: boolean | null
  isAd: boolean | null
  isLive: boolean | null
  loading: boolean

  onClickPlay: () => void
  onClickPause: () => void
  onClickFastForward: () => void
  onClickRewind: () => void
  onClickCastButton: () => void
  onSeek: (targetPercent: number) => void
  onSelectSubtitle: (trackId: number) => void
  castState: 'CONNECTED' | 'CONNECTING' | 'NO_DEVICES_AVAILABLE' | 'NOT_CONNECTED' | undefined
  deviceName: string
};

const RemotePlayer: React.FC<RemotePlayerProps> = ({
  currentTime = 0,
  duration = 0,
  isPaused,
  timelineMarkers,
  onClickPlay,
  onClickPause,
  onClickRewind,
  onClickFastForward,
  onSeek,
  onClickCastButton,
  isAd = false,
  onSelectSubtitle,
  playbackData,
  tracks,
  isLive = false,
  loading,
  castState,
  deviceName,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('common');

  // Mini player does not currently have minimised state - see ODWEB-2822
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [minimised, setMinimised] = useState(false);

  const formattedCurrentTime = Duration.fromMillis(currentTime * 1000).toFormat('hh:mm:ss');
  const formattedDuration = Duration.fromMillis(duration * 1000).toFormat('hh:mm:ss');

  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'subtitleMenu',
  });

  let videoImageUrl = null;

  if (playbackData?.ogImageId) {
    videoImageUrl = `https://image.pr.sbsod.com/${playbackData.ogImageId}`;
    videoImageUrl += '?crop=true&width=300&height=169&type=jpeg';
  }

  let statusLabel: string;

  switch (true) {
    case (castState === 'NO_DEVICES_AVAILABLE'):
      statusLabel = t('videoPlayer.chromecastNoDevicesAvailable');
      break;

    case (castState === 'CONNECTING'):
      statusLabel = t('videoPlayer.chromecastConnectingTo', { device: deviceName });
      break;

    case (loading === true):
      statusLabel = t('videoPlayer.chromecastLoadingMediaTo', { device: deviceName });
      break;

    case (isPaused === true):
      statusLabel = t('videoPlayer.chromecastPausedOn', { device: deviceName });
      break;

    case (isPaused === false):
      statusLabel = t('videoPlayer.chromecastPlayingOn', { device: deviceName });
      break;

    case (castState === 'CONNECTED'):
      statusLabel = t('videoPlayer.chromecastConnectedTo', { device: deviceName });
      break;

    default:
      statusLabel = null;
      break;
  }

  return (
    <div className={clsx(classes.container, minimised && classes.minimised)}>
      <div className={classes.topSection}>
        {(!loading && !isAd && !isLive) && (
          <>
            <div data-testid="formattedCurrentTime">{formattedCurrentTime}</div>
            <SeekBar onSeek={onSeek} currentTime={currentTime} duration={duration} breaks={timelineMarkers}/>
            <div data-testid="formattedDuration">{formattedDuration}</div>
          </>
        )}
        {isAd && <div>Playing an advertisement</div>}
      </div>
      <div className={classes.controlsSection}>

        <div className={classes.mediaInfoSection}>
          {
            videoImageUrl
              ? (
                <div
                  className={classes.mediaInfoImage}
                  style={{ backgroundImage: `url('${videoImageUrl}')` }}
                />
              )
              : null
          }
          <div className={classes.mediaInfoTextContainer}>
            <div className={classes.mediaInfoGroup}>
              <h3 className={classes.mediaInfoTitle}>{playbackData?.title}</h3>
              <p className={classes.mediaInfoSubtitle}>{playbackData?.subtitle}</p>
            </div>
            <div>
              <div className={classes.castGroup}>
                <button
                  className={clsx(classes.toggleButton, classes.toggleButtonActive)}
                  type="button"
                  aria-label={statusLabel}
                  onClick={onClickCastButton}
                />
                <div className={clsx(classes.statusLabel)}>
                  {statusLabel}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={classes.smCastContainer}>
          <div>
            <button
              className={clsx(classes.toggleButton, classes.toggleButtonActive)}
              type="button"
              aria-label={statusLabel}
              onClick={onClickCastButton}
            />
          </div>
        </div>

        <div className={classes.playbackControlsSection}>
          <div className={classes.playbackControlsContainer}>
            {!loading
              ? (
                <>
                  <button
                    className={clsx(classes.seekButton, classes.seekBackButton, classes.controlsButton)}
                    aria-label={t('videoPlayer.rewind')}
                    type="button"
                    name="rewind"
                    onClick={onClickRewind}
                    disabled={isAd || isLive}
                  />
                  {
                    isPaused
                      ? (
                        <button className={clsx(classes.playButton, classes.controlsButton)} aria-label={t('videoPlayer.playVideo')} type="button" name="play" onClick={onClickPlay}/>
                      ) : (
                        <button className={clsx(classes.pauseButton, classes.controlsButton)} aria-label={t('videoPlayer.pauseVideo')} type="button" name="pause" onClick={onClickPause}/>
                      )
                  }
                  <button
                    className={clsx(classes.seekButton, classes.seekForwardButton, classes.controlsButton)}
                    aria-label={t('videoPlayer.fastForward')}
                    type="button"
                    name="fastForward"
                    onClick={onClickFastForward}
                    disabled={isAd || isLive}
                  />
                </>
              )
              : (
                <div className={classes.loadingSpinner}>
                  <CircularProgress color="secondary" size={60}/>
                </div>
              )}
          </div>
        </div>

        {tracks?.length > 0 && (
          <div className={classes.settingsSection}>
            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
            <button className={clsx(classes.controlsButton, classes.settingsSubtitles)} aria-label={t('videoPlayer.subtitlesAndAudioDescription')} type="button" name="subtitlesAndAudioDescription" {...bindHover(popupState)}/>
            <HoverMenu
              PaperProps={{
                style: {
                  padding: '1rem 1.5rem',
                  width: 240,
                  backdropFilter: 'blur(10px) saturate(2%)',
                  backgroundColor: 'rgba(26, 32, 36, 0.8)',
                  zIndex: 9999,
                },
              }}
              style={{ top: -110, left: -10 }}
              {...bindMenu(popupState)}
              disableScrollLock
            >
              <div className={classes.subtitlesMenuHeading}>Subtitles</div>
              {
                tracks?.map((track) => {
                  return (
                    <MenuItem
                      key={track.trackId}
                      className={clsx(
                        track.active && classes.activeTrack,
                      )}
                      onClick={() => {
                        onSelectSubtitle(track.trackId);
                      }}
                    >
                      {track.name}
                    </MenuItem>
                  );
                })
              }
            </HoverMenu>
          </div>
        )}
      </div>
    </div>
  );
};

export default RemotePlayer;
